import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, XCircle, Trash } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);
const defaultDate = () => {
  const d = new Date(Date.now() + 4 * 3600 * 1000); // Dubai time
  if (d.getUTCHours() >= 9) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

export function FedexPickupButton({ preselect = [], className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" className={`rounded-sm border-[#4D148C]/40 text-[#4D148C] hover:bg-[#4D148C]/5 ${className}`} onClick={() => setOpen(true)} data-testid="fedex-pickup-btn">
        <Truck size={16} className="mr-2" /> FedEx pickup
      </Button>
      <FedexPickupDialog open={open} onOpenChange={setOpen} preselect={preselect} />
    </>
  );
}

export function FedexPickupDialog({ open, onOpenChange, preselect = [] }) {
  const [cands, setCands] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [sel, setSel] = useState({});
  const [form, setForm] = useState({ ready_date: defaultDate(), ready_time: "10:00", close_time: "18:00", total_weight_kg: "", remarks: "", notification_emails: "ops@heliopolispharmaceuticals.com" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/fedex/pickup-candidates"), api.get("/fedex/pickups")]);
    setCands(c.data); setPickups(p.data.filter((x) => x.status === "scheduled" || (x.cancelled_at && Date.now() - new Date(x.cancelled_at).getTime() < 7 * 86400000)));
    setSel(Object.fromEntries(c.data.filter((s) => preselect.includes(s.id) && s.pickup?.status !== "scheduled").map((s) => [s.id, true])));
  };
  useEffect(() => { if (open) load().catch(() => toast.error("Could not load FedEx shipments")); }, [open]); // eslint-disable-line

  const chosen = cands.filter((s) => sel[s.id]);
  const submit = async () => {
    if (!chosen.length) return toast.error("Select at least one shipment");
    if (!(Number(form.total_weight_kg) > 0)) return toast.error("Enter the total weight of the collection");
    setBusy(true);
    try {
      const emails = form.notification_emails.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
      if (emails.some((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))) return toast.error("Check the confirmation email address(es)");
      const { data } = await api.post("/fedex/pickups", { ...form, notification_emails: emails, total_weight_kg: Number(form.total_weight_kg), shipment_ids: chosen.map((s) => s.id) });
      toast.success(`FedEx pickup booked in ${data.env} — confirmation ${data.confirmation_code}${data.location ? ` (location ${data.location})` : ""} for ${data.package_count} package(s). FedEx will email the confirmation to ${(data.notification_emails || []).join(", ") || "the addresses given"}.`, { duration: 12000 });
      setForm((f) => ({ ...f, total_weight_kg: "", remarks: "" }));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "FedEx pickup failed", { duration: 9000 }); }
    finally { setBusy(false); }
  };
  const cancel = async (p, force = false) => {
    if (!window.confirm(force ? `Remove pickup ${p.confirmation_code} from the list without FedEx confirmation?` : `Cancel FedEx pickup ${p.confirmation_code}?`)) return;
    try { const { data } = await api.post(`/fedex/pickups/${p.id}/cancel${force ? "?force=true" : ""}`); toast.success(data.message, { duration: 8000 }); await load(); }
    catch (e) {
      const msg = e?.response?.data?.detail || "Cancel failed";
      toast.error(msg, { duration: 10000, action: force ? undefined : { label: "Remove anyway", onClick: () => cancel(p, true) } });
    }
  };
  const remove = async (p) => {
    if (!window.confirm(`Remove pickup ${p.confirmation_code} from the list?`)) return;
    try { await api.delete(`/fedex/pickups/${p.id}`); toast.success("Pickup removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="fedex-pickup-dialog">
        <DialogHeader><DialogTitle>Request FedEx pickup</DialogTitle></DialogHeader>
        <div className="space-y-5 text-sm">
          <section>
            <h4 className="font-heading font-bold mb-2">Shipments to collect <span className="text-muted-foreground font-normal">({chosen.length} selected)</span></h4>
            {cands.length === 0 ? <p className="text-muted-foreground">No FedEx shipments awaiting collection (needs a FedEx AWB and status Preparing/Dispatched).</p> : (
              <div className="max-h-56 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {cands.map((s) => {
                  const booked = s.pickup?.status === "scheduled";
                  return (
                    <label key={s.id} className={`flex items-center gap-3 px-3 py-2 ${booked ? "opacity-60" : "cursor-pointer hover:bg-muted/40"}`} data-testid={`pickup-cand-${s.shipment_number}`}>
                      <input type="checkbox" className="h-4 w-4 accent-primary" disabled={booked} checked={!!sel[s.id]} onChange={(e) => setSel({ ...sel, [s.id]: e.target.checked })} data-testid={`pickup-check-${s.shipment_number}`} />
                      <span className="font-semibold w-24 shrink-0">{s.shipment_number}</span>
                      <span className="truncate flex-1">{s.client_name}{s.patient_name ? ` · ${s.patient_name}` : ""}</span>
                      <span className="font-mono text-xs text-muted-foreground">{s.tracking_number}</span>
                      {booked && <span className="text-[10px] uppercase font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">Pickup {s.pickup.date}</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </section>
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs uppercase tracking-wide">Pickup date</Label><Input type="date" min={today()} className="mt-1.5 rounded-sm" value={form.ready_date} onChange={(e) => setForm({ ...form, ready_date: e.target.value })} data-testid="pickup-date" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Ready from</Label><Input type="time" className="mt-1.5 rounded-sm" value={form.ready_time} onChange={(e) => setForm({ ...form, ready_time: e.target.value })} data-testid="pickup-ready" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Office closes</Label><Input type="time" className="mt-1.5 rounded-sm" value={form.close_time} onChange={(e) => setForm({ ...form, close_time: e.target.value })} data-testid="pickup-close" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Total weight (kg)</Label><Input type="number" step="0.1" min="0" className="mt-1.5 rounded-sm" value={form.total_weight_kg} onChange={(e) => setForm({ ...form, total_weight_kg: e.target.value })} data-testid="pickup-weight" /></div>
          </section>
          <div><Label className="text-xs uppercase tracking-wide">Remarks for the courier (optional)</Label><Textarea rows={2} className="mt-1.5 rounded-sm" maxLength={60} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. Collect at reception, Flat 605" data-testid="pickup-remarks" /></div>
          <div>
            <Label className="text-xs uppercase tracking-wide">FedEx confirmation email(s)</Label>
            <Input className="mt-1.5 rounded-sm" value={form.notification_emails} onChange={(e) => setForm({ ...form, notification_emails: e.target.value })} placeholder="ops@heliopolispharmaceuticals.com, another@…" data-testid="pickup-notification-emails" />
            <p className="text-xs text-muted-foreground mt-1">FedEx sends the pickup confirmation (and any changes) to these addresses. Separate several with commas — max 5.</p>
          </div>
          <p className="text-xs text-muted-foreground">Pickup address: Joseph Gadalla · Oasis B Block, Flat 605, Al Mankhool, Bur Dubai · 0547741436. One collection covers all selected shipments.</p>
          {pickups.length > 0 && (
            <section>
              <h4 className="font-heading font-bold mb-2">Scheduled pickups</h4>
              <div className="space-y-2">
                {pickups.map((p) => (
                  <div key={p.id} className={`flex items-center gap-3 border border-border rounded-sm px-3 py-2 ${p.status !== "scheduled" ? "opacity-70" : ""}`} data-testid={`pickup-row-${p.confirmation_code}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{p.scheduled_date} · {p.ready_time}–{p.close_time} · {p.package_count} pkg · <span className="font-mono">{p.confirmation_code}</span>
                        {p.status !== "scheduled" && <span className="ml-2 text-[10px] uppercase tracking-wide rounded-sm bg-muted px-1.5 py-0.5" data-testid={`pickup-status-${p.confirmation_code}`}>{p.status}</span>}
                        {p.stale_env && <span className="ml-2 text-[10px] uppercase tracking-wide rounded-sm bg-amber-100 text-amber-800 px-1.5 py-0.5" title={`Booked in FedEx ${p.env} — not visible in FedEx's live system`} data-testid={`pickup-env-${p.confirmation_code}`}>{p.env} — not in FedEx live</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{p.shipments.map((s) => s.shipment_number).join(", ")}{p.cancel_message ? ` · ${p.cancel_message}` : ""}</p>
                    </div>
                    {p.status === "scheduled"
                      ? <button className="text-muted-foreground hover:text-destructive" onClick={() => cancel(p)} title="Cancel pickup" data-testid={`pickup-cancel-${p.confirmation_code}`}><XCircle size={18} /></button>
                      : <button className="text-muted-foreground hover:text-destructive" onClick={() => remove(p)} title="Remove from list" data-testid={`pickup-remove-${p.confirmation_code}`}><Trash size={18} /></button>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
          <Button className="rounded-sm" disabled={busy || !chosen.length} onClick={submit} data-testid="pickup-submit">{busy ? "Booking with FedEx…" : `Book pickup (${chosen.length})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
